import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { FormGroup, FormBuilder, Validators, AbstractControl, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { IAccount } from '../../../../models/account.interface';
import { AuthService } from './../../../../services/auth/auth.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { catchError } from 'rxjs/operators';

class CrossFieldErrorMatcherPassword implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (control.touched && control.errors) || (form.errors?.password);
  }
}

@Component({
    selector: 'ebb-activate',
    templateUrl: './activate.component.html',
    styleUrls: ['./activate.component.scss'],
    standalone: false
})
export class ActivateComponent implements OnInit {
  public accountForm: FormGroup;
  public emailSendSuccess: boolean;
  public requestCounter = 0;
  public requesting: boolean;
  public username: string;
  public delayInterval: NodeJS.Timer;
  public passwordMatcherEmail = new CrossFieldErrorMatcherPassword();
  public emailSendError = false;
  public messageDisplay: string;
  public messageDisplayShowLogout = false;
  public isloading = false;
  @Input() contact$: Observable<any>;
  @Input() set contactData(value: { firstName: string,  lastName: string, email: string  }) {
    this.username = value.email;
  }
  @Input() tooManyAttempts: boolean;
  @Output() accountInfo: EventEmitter<IAccount> = new EventEmitter<IAccount>();
  @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()set message(message: string) {
    this.messageDisplay = message;
    this.messageDisplayShowLogout = false;
  
    if(message === 'success' || message === ''){
      this.messageDisplay = '';
      this.messageDisplayShowLogout = true;
      return;
    }

    if(message === 'bad email code'){
      this.messageDisplay = 'Invalid Confirmation Email Code Entered';
      return;
    }
    if(message === 'Another object with the same value for property userPrincipalName already exists.'){
      this.messageDisplay = 'That email address is already registered with the program';
      this.messageDisplayShowLogout = true;
    }
  }

  public _activateNextInProcess = false;
  @Input() set activateNextInProcessComplete(value: boolean) {
    this._activateNextInProcess = !value;
  }

  constructor(
    private readonly _fb: FormBuilder,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loading.emit(true);
    this.requestNewCode(null);
    this.accountForm = this._fb.group({
      displayName: { value: this.username, disabled: true },
      emailCode: [null, Validators.required],
      password: [null, [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(100),
        Validators.pattern(/([A-Z]){1,}/),
        Validators.pattern(/([a-z]){1,}/),
        Validators.pattern(/([0-9]){1,}/),
        Validators.pattern(/([~`!@#$%\^\&*\)\()]){1,}/)
      ]],
      confirmPassword: [null, Validators.required],
    }, { validators: this.confirmValidator });
  }

  public submitAccount(): void {
    this._activateNextInProcess = true;
    this.isloading = true;
    const submittedValue = this.accountForm.getRawValue();
    this.message = '';
    delete submittedValue['confirmPassword'];
    this.accountInfo.emit(submittedValue);
    setTimeout(() => {
      this.isloading = false;
    
    }, 2000); 
    
  }

  public requestNewCode(e): void {
    this.requesting = true;
    if (this.requestCounter === 2) {
      this.codeRequestDelay();
    }
    if (this.requestCounter < 3) {
      this.contact$.subscribe(({ saveContact }) => {
        this.authService.sendVerifyEmail().pipe(
          catchError(error => {

            if (error.message.includes('not authenticated')) {
              this.route.queryParams.subscribe(params => {
                const idParam = params['id'];
                const emailParam = params['email'];
                this.router.navigate(['register', 'login'], {
                  queryParams: { id: idParam, email: emailParam }
                });
              });
            } else {
            console.error('Error sending email', error);
            this.emailSendError = true;
            this.requesting = false;
            this.loading.emit(false);}
            return of(null); // Return a fallback observable to keep the stream alive
          })
        ).subscribe(res => {
          if (this.emailSendSuccess) {
            this.requestCounter++;
          }
          if (res.status && !this.emailSendSuccess) {
            this.emailSendSuccess = res.status;
            this.loading.emit(res.status);
          }
          this.emailSendError = false;
          this.requesting = false;
          this.loading.emit(false);
        }, () => {
          console.error('error sending email');
          this.emailSendError = true;
          this.requesting = false;
          this.loading.emit(false);
        });
      });
    }
    if(e) {e.preventDefault();}
  }

  public logout(e): void {
    const redirectEndpoint = (this.router.url.split('?')[0]).replace('//','/' );
    this.authService.logout(redirectEndpoint).subscribe(url => {
      document.location.href = url;
    });
    e.preventDefault();
  }

  private codeRequestDelay(): void {
    if (!this.delayInterval) {
      this.delayInterval = setTimeout(() => {
        this.requestCounter = 0;
        this.requesting = false;
        this.delayInterval = null;
      }, 300000);
    }
  }

  private confirmValidator(c: AbstractControl): { password: boolean } {
    if (c.get('confirmPassword').value && c.get('password').value !== c.get('confirmPassword').value) {
      return { password: true }
    }
  }
  
  public continueEnabled(): boolean {
    if (this.accountForm.valid && !this.tooManyAttempts && !this._activateNextInProcess) {
      return true;
    }
    return false;
  }

}
